import React, { FC } from 'react';
import { Text, withSitecoreContext, RichText, Placeholder, ComponentRendering, ComponentConsumerProps } from '@sitecore-jss/sitecore-jss-react';
import CTAButton, { ctaEnums } from '../../@shared/ctaButton';
import RoutableSitecoreLink from '../../@shared/routableSitecoreLink';
import Icon from '../../@shared/icon';
import { CustomText } from '../../@shared/customText';
import Picture from '../../@shared/picture';
import UnreleasedComponent from '../../@shared/unreleasedComponent';
import { TextModuleModel } from './models/textModuleContainer.model';
import { TextModuleProps } from './types/textModuleContainer.types';
import useMediaQuery from '../../../lib/custom-hooks/useMediaQuery';
import { BreakpointsInPx } from '../../../constants/breakpoints.constants';
import { googleClickCtaEvent, googleClickRichTextEvent } from '../../../utils/analytics';
import { targetLinkdsk } from '../../../utils/targetDskLink';
import { iconSize } from '../../../constants/icon.constants';
import { getQueryLink } from '../../../utils/getQuerylink';

const TextModuleContainer: FC<ComponentConsumerProps & TextModuleProps> = (props) => {
  const { iconName = '', extraClassName = '', trackAnalyticsEvents = true, showGenericPopup = true } = props;
  const isEE = props.sitecoreContext?.pageEditing;
  const model = new TextModuleModel(props, isEE);
  const isDesktop = useMediaQuery(`(min-width: ${BreakpointsInPx['md']})`);
  const { imageWidth } = model.getLoadedImageDimension(isDesktop);
  model?.link1 && getQueryLink(model?.link1)
  model?.link2 && getQueryLink(model?.link2)

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  return (
    <div className={`text-module text-module--${model.textPosition?.value} text-module--${model.backgroundColor?.value} ${extraClassName}`}>
      <div className={`text-module__content text-module__content--${model.textPosition?.value}`}>
        <div
          style={{
            maxWidth: `min(${imageWidth}px, 100%)`,
            marginLeft: model.textPosition?.value === 'left' ? 0 : 'auto',
            marginRight: model.textPosition?.value === 'right' ? 0 : 'auto',
          }}
        >
          <Picture
            editorialImageProps={{
              desktopImage: model.image,
              mobileImage: model.mobileImage,
              desktopTransformation: 'Size1000',
              mobileTransformation: 'Size1000',
              cssClassPictureEl: 'text-module__image-wrapper',
              alt: model.image?.value?.alt as string,
            }}
          />
        </div>
        {iconName && (
          <div className="text-module__icon-wrapper">
            <Icon iconName={iconName} size={iconSize.xlg} color="success" />
          </div>
        )}
        {model.eyebrow && <Text tag="p" field={model.eyebrow} className={'text-module__eyebrow'} />}
        <CustomText tag={model.headingTag} field={model.title} className={'text-module__title'} />
        {model.subtitle && (
          <RichText
            tag="div"
            field={model.subtitle}
            className={'text-module__subtitle rich-text'}
            onClick={(event) => trackAnalyticsEvents && googleClickRichTextEvent(event, model.title?.value ?? model.eyebrow?.value)}
          />
        )}
        {model.bodyText && (
          <RichText
            tag="div"
            field={model.bodyText}
            className={'text-module__text rich-text color-primary-link'}
            onClick={(event) => trackAnalyticsEvents && googleClickRichTextEvent(event, model.title?.value ?? model.eyebrow?.value)}
          />
        )}
        <div className={'text-module__buttons'}>
          {model?.link1ExistsOrIsEE && (
            <CTAButton type={ctaEnums.primaryRed}>
              <RoutableSitecoreLink
                className="text-module__primary-link"
                field={model?.link1}
                target={targetLinkdsk(model?.link1, isDesktop)}
                onClick={() => trackAnalyticsEvents && googleClickCtaEvent('Button', model?.link1?.value.text ?? model?.link1?.value.title, model.title?.value)}
              />
            </CTAButton>
          )}
          {model?.link2ExistsOrIsEE && (
            <div className={'text-module__secondary-link'}>
              <RoutableSitecoreLink
                field={model?.link2}
                target={targetLinkdsk(model?.link2, isDesktop)}
                onClick={() => trackAnalyticsEvents && googleClickCtaEvent('Button', model?.link2?.value.text ?? model?.link2?.value.title, model.title?.value)}
              />
              {(model?.link2?.value?.text || model?.link2?.value?.href) && (
                <span className="link-with-arrow link-with-arrow--text">
                  <Icon iconName="arrow-simple-right" size={24} color="primary" />
                </span>
              )}
            </div>
          )}
          {showGenericPopup && <div className={'text-module__pop-up ' + (isEE ? 'text-module__pop-up--EE' : '')}>
            <Placeholder name={'jss-generic-popup'} rendering={props.rendering as ComponentRendering} />
          </div>}
        </div>
      </div>
    </div>
  );
};

export default withSitecoreContext()(TextModuleContainer);
